import { useQueryClient } from '@tanstack/react-query';
import { hydraUiApi, useHydraUIAPIMutation, useHydraUIAPIQuery } from '@/services/api';
import { useAuth } from '@/services/auth';
import { isFlexPlan } from '@/services/auth/plan-slugs';
import type { BillingAutoTopUpSettings } from '@customerio/hydra-ui-api';
import { DEFAULT_AUTO_TOP_UP_SETTINGS, type AutoTopUpSettings } from '../utils/add-funds';

export type AutoTopUp =
  | { status: 'loading' }
  | { status: 'unavailable' }
  | { status: 'ready'; hasCard: boolean; settings: AutoTopUpSettings; disabledReason: string | null };

export const AUTO_TOP_UP_QUERY_KEY = ['getWalletAutoTopUp'];

function autoTopUpQueryKey(accountId: number) {
  return [...AUTO_TOP_UP_QUERY_KEY, { params: { account_id: accountId } }];
}

export function useAutoTopUp(): AutoTopUp {
  const { account } = useAuth();
  const isFlex = isFlexPlan(account);

  const { data, isLoading, isError } = useHydraUIAPIQuery({
    queryKey: autoTopUpQueryKey(account.id),
    queryFn: ({ signal }) =>
      hydraUiApi.billing.getWalletAutoTopUp({ params: { path: { account_id: String(account.id) } }, signal }),
    queryConfig: { enabled: isFlex },
  });

  if (!isFlex || isError) return { status: 'unavailable' };
  if (isLoading || data == null) return { status: 'loading' };

  return {
    status: 'ready',
    hasCard: data.has_card,
    settings: savedSettings(data.settings),
    disabledReason: data.settings?.disabled_reason ?? null,
  };
}

function savedSettings(settings: BillingAutoTopUpSettings | undefined): AutoTopUpSettings {
  if (settings == null) return DEFAULT_AUTO_TOP_UP_SETTINGS;
  return {
    enabled: settings.enabled,
    threshold_usd: settings.threshold_usd,
    target_usd: settings.target_usd,
    period_cap_enabled: settings.period_cap_enabled,
    // Keeps an amount in the field for when the limit is turned back on.
    period_cap_usd: settings.period_cap_usd ?? DEFAULT_AUTO_TOP_UP_SETTINGS.period_cap_usd,
  };
}

export function useSaveAutoTopUp() {
  const { account } = useAuth();
  const queryClient = useQueryClient();

  return useHydraUIAPIMutation(
    (settings: AutoTopUpSettings) =>
      hydraUiApi.billing.updateWalletAutoTopUp({
        params: { path: { account_id: String(account.id) } },
        body: settings,
      }),
    {
      // The save answers with the settings as the GET reports them.
      onSuccess: (response) => {
        queryClient.setQueryData(autoTopUpQueryKey(account.id), response);
      },
    },
  );
}
